import { useEffect, useMemo } from 'react'
import {
    useQueryFormsQuery,
    useCountFormsQuery,
    FormFieldsFragment,
    OrderByInput,
} from '~graphql/generated/graphql'
import { getCleanedQueryParams } from '~utils/get-cleaned-query-params'
import {
    eqBoolFilter,
    eqStringFilter,
    inStringArrFilter,
    searchFilter,
} from '~utils/query-helpers'

export function useFormQueries(
    clinicIds: string[],
    showArchived?: boolean,
    page?: number,
    pageSize?: number,
    search?: string,
): {
    results: FormFieldsFragment[]
    loading: boolean
    count: number
} {
    const hasClinicScope = clinicIds.length > 0
    const filters = [
        inStringArrFilter('clinic', clinicIds),
        ...(showArchived ? [] : [eqBoolFilter('is_archived', false)]),
        ...searchFilter('name', search || ''),
    ]

    const { data: countFormsData, loading: countFormsLoading } =
        useCountFormsQuery({
            variables: {
                where: filters,
            },
            skip: !hasClinicScope,
            onError: err => {
                console.error(
                    'useFormQueries (useCountFormsLazyQuery) Error getting forms: ',
                    err,
                )
            },
            fetchPolicy: 'no-cache',
        })

    const {
        data: queryFormsData,
        loading: allClientsLoading,
        refetch: refetchForms,
    } = useQueryFormsQuery({
        variables: {
            where: filters,
            page: page,
            pageSize: pageSize || 10,
            orderBy: {
                column: 'name',
                isAscending: true,
            },
        },
        skip: !hasClinicScope,
        onError: err => {
            console.error(
                'useSegmentQueries (useQueryFormsLazyQuery) Error getting forms: ',
                err,
            )
        },
        fetchPolicy: 'no-cache',
    })

    useEffect(() => {
        refetchForms({
            page: page,
        })
    }, [page])

    const results = queryFormsData?.queryForms || []
    const count = countFormsData?.countForms || 0

    const loading = allClientsLoading || countFormsLoading

    return {
        results: results,
        loading: loading,
        count: count,
    }
}

/**
 * Forms owned by a clinic group itself (shared forms that waterfall to
 * linked clinic copies — see entity links), as opposed to the per-clinic
 * aggregation above.
 *
 * `loading` is true only until the first result arrives; later sort / filter
 * changes keep the previous results while they load.
 */
export function useGroupFormQueries(
    clinicGroupId: string,
    showArchived?: boolean,
    search?: string,
    orderBy: OrderByInput = { column: 'name', isAscending: true },
): {
    results: FormFieldsFragment[]
    loading: boolean
    count: number
} {
    const filters = [
        eqStringFilter('clinic_group', clinicGroupId),
        ...(showArchived ? [] : [eqBoolFilter('is_archived', false)]),
        ...searchFilter('name', search || ''),
    ]

    const {
        data: currentCountFormsData,
        previousData: previousCountFormsData,
        loading: countFormsLoading,
    } = useCountFormsQuery({
        variables: {
            where: filters,
        },
        onError: err => {
            console.error(
                'useGroupFormQueries (useCountFormsQuery) Error getting forms: ',
                err,
            )
        },
        fetchPolicy: 'no-cache',
    })

    const {
        data: currentQueryFormsData,
        previousData: previousQueryFormsData,
        loading: queryFormsLoading,
    } = useQueryFormsQuery({
        variables: {
            where: filters,
            page: 0,
            pageSize: 1000,
            orderBy,
        },
        onError: err => {
            console.error(
                'useGroupFormQueries (useQueryFormsQuery) Error getting forms: ',
                err,
            )
        },
        fetchPolicy: 'no-cache',
    })

    const queryFormsData = currentQueryFormsData ?? previousQueryFormsData
    const countFormsData = currentCountFormsData ?? previousCountFormsData
    const hasLoadedForms = !!queryFormsData && !!countFormsData

    return {
        results: queryFormsData?.queryForms || [],
        loading: !hasLoadedForms && (queryFormsLoading || countFormsLoading),
        count: countFormsData?.countForms || 0,
    }
}
